// ============================================================
// AccessStudio.jsx
// The real foundation for in-context permissions.
//
// Exports:
//   <AccessStudioProvider>  wrap your app once (near the top, inside auth)
//   <Gate permission="bus.print_card.print" label="Print Card"> ... </Gate>
//   <AccessToolbar/>         floating Use / Annotate / Preview bar (admins only)
//   useAccessStudio()        if you need the studio state directly
//
// HOW IT WORKS
//   • In normal use, <Gate> behaves exactly like your old
//     {hasPermission('bus','print_card','print') && <button/>} — it shows the
//     child only if the current user has that permission. Drop-in replacement.
//   • For a GlobalAdmin, the toolbar unlocks two extra modes:
//       Annotate  – every <Gate> draws a box you click to assign roles
//       Preview   – see the screen exactly as a chosen role would
//   • Assigning a role writes through your EXISTING endpoint
//     PUT /permissions/roles/:id, so the overlay, the matrix, and the role
//     editor are all one source of truth.
//
// It depends on nothing but the props you pass the provider, so it doesn't
// care how your PermissionContext is wired.
// ============================================================

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Lock, X, Check, Crown, Eye, MousePointerClick, Users } from "lucide-react";

const SUPERUSER_ROLES = ["GlobalAdmin"];

const StudioCtx = createContext(null);
export const useAccessStudio = () => useContext(StudioCtx);

const ACTION_TONE = {
  view: "bg-sky-100 text-sky-700 border-sky-200",
  create: "bg-emerald-100 text-emerald-700 border-emerald-200",
  edit: "bg-amber-100 text-amber-700 border-amber-200",
  delete: "bg-rose-100 text-rose-700 border-rose-200",
  print: "bg-violet-100 text-violet-700 border-violet-200",
  process: "bg-teal-100 text-teal-700 border-teal-200",
};
const toneFor = (a) => ACTION_TONE[a] || "bg-slate-100 text-slate-600 border-slate-200";

// ------------------------------------------------------------
// Provider
//   props:
//     apiCall            your fetch wrapper
//     user               { roles: [...] } — the current user
//     userHasPermission  (module, feature, action) => bool  (your existing hasPermission)
//     enabled            optional; default true. Set false to force plain 'use'.
// ------------------------------------------------------------
export function AccessStudioProvider({ apiCall, user, userHasPermission, pastYear = false, enabled = true, children }) {
  const isAdmin = !!user?.roles?.some((r) => SUPERUSER_ROLES.includes(r));
  const adminEnabled = enabled && isAdmin;
  // The selected year is a locked PAST year for this user — admins bypass the
  // lock, so write buttons only get disabled for non-admins.
  const yearReadOnly = pastYear && !isAdmin;

  const [mode, setMode] = useState("use");          // use | annotate | preview
  const [previewRole, setPreviewRole] = useState(null);
  const [roles, setRoles] = useState([]);
  const [resolver, setResolver] = useState(null);   // "module.feature.action" -> {featureId, actionId}
  const [grants, setGrants] = useState({});         // roleCode -> Set("featureId_actionId")
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load catalog + every role's permission set — admin only, once.
  useEffect(() => {
    if (!adminEnabled) return;
    let cancelled = false;
    (async () => {
      try {
        const [rolesData, modules, features, actions] = await Promise.all([
          apiCall("/roles"),
          apiCall("/permissions/modules"),
          apiCall("/permissions/features"),
          apiCall("/permissions/actions"),
        ]);
        // build code -> id resolver
        const modById = Object.fromEntries(modules.map((m) => [m.ModuleCode, m.ModuleID]));
        const actById = Object.fromEntries(actions.map((a) => [a.ActionCode, a.ActionID]));
        const featById = {};
        features.forEach((f) => { featById[`${f.ModuleID}.${f.FeatureCode}`] = f.FeatureID; });
        const resolve = (perm) => {
          const [m, f, a] = perm.split(".");
          const moduleId = modById[m];
          const featureId = featById[`${moduleId}.${f}`];
          const actionId = actById[a];
          return featureId && actionId ? { featureId, actionId } : null;
        };
        // load each role's grants
        const grantMap = {};
        await Promise.all(
          rolesData.map(async (r) => {
            try {
              const perms = await apiCall(`/permissions/roles/${r.RoleID}`);
              grantMap[r.RoleName] = new Set(perms.map((p) => `${p.FeatureID}_${p.ActionID}`));
            } catch {
              grantMap[r.RoleName] = new Set();
            }
          })
        );
        if (cancelled) return;
        setRoles(rolesData.map((r) => ({ code: r.RoleName, id: r.RoleID, label: r.RoleName, superuser: SUPERUSER_ROLES.includes(r.RoleName) })));
        setResolver(() => resolve);
        setGrants(grantMap);
        setPreviewRole(rolesData.find((r) => !SUPERUSER_ROLES.includes(r.RoleName))?.RoleName || null);
        setReady(true);
      } catch (e) {
        console.error("AccessStudio load failed:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [adminEnabled, apiCall]);

  const roleHas = useCallback((roleCode, perm) => {
    const role = roles.find((r) => r.code === roleCode);
    if (role?.superuser) return true;
    if (!resolver) return false;
    const ids = resolver(perm);
    if (!ids) return false;
    return grants[roleCode]?.has(`${ids.featureId}_${ids.actionId}`) || false;
  }, [roles, resolver, grants]);

  const rolesWith = useCallback((perm) => {
    return roles.filter((r) => r.superuser || roleHas(r.code, perm));
  }, [roles, roleHas]);

  // Toggle one role on/off for one permission, then persist that role's full set.
  const toggleRole = useCallback(async (perm, roleCode) => {
    const role = roles.find((r) => r.code === roleCode);
    if (!role || role.superuser || !resolver) return;
    const ids = resolver(perm);
    if (!ids) { console.warn("Unknown permission (not seeded?):", perm); return; }
    const key = `${ids.featureId}_${ids.actionId}`;
    const next = new Set(grants[roleCode] || []);
    next.has(key) ? next.delete(key) : next.add(key);
    setGrants((g) => ({ ...g, [roleCode]: next }));
    setSaving(true);
    try {
      const permissions = [...next].map((k) => {
        const [featureId, actionId] = k.split("_").map(Number);
        return { featureId, actionId };
      });
      await apiCall(`/permissions/roles/${role.id}`, { method: "PUT", body: JSON.stringify({ permissions }) });
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setSaving(false);
    }
  }, [roles, resolver, grants, apiCall]);

  // Should admin-only elements be visible right now?
  //   use mode / non-admin -> only if the current user is a superuser
  //   preview mode          -> only if the PREVIEWED role is a superuser
  //   annotate mode         -> yes (so the admin can navigate & see them)
  let showAdminOnly;
  if (adminEnabled && mode === "preview") {
    showAdminOnly = !!roles.find((r) => r.code === previewRole)?.superuser;
  } else if (adminEnabled && mode === "annotate") {
    showAdminOnly = true;
  } else {
    showAdminOnly = isAdmin;
  }

  const value = {
    adminEnabled, ready, mode, setMode, previewRole, setPreviewRole,
    roles, roleHas, rolesWith, toggleRole, saving,
    isSuperuser: isAdmin, showAdminOnly,
    // 'use'-mode check for the current user:
    userHasPermission,
    yearReadOnly,
  };

  return <StudioCtx.Provider value={value}>{children}</StudioCtx.Provider>;
}

// ------------------------------------------------------------
// <Gate permission="module.feature.action" label="Human label">child</Gate>
//   or  <Gate adminOnly label="Bulk Transfer">child</Gate>  for tools that
//   are intentionally restricted to superusers (not granular permissions).
// ------------------------------------------------------------
// Actions that MODIFY data — disabled on a locked past year. Read-ish actions
// (view/print/export/download) are intentionally NOT here so they stay usable.
const WRITE_ACTIONS = new Set([
  "create", "edit", "delete", "process", "configure", "manage", "record", "enter", "transition",
]);

// Modules NOT governed by the global year switcher, so their write buttons stay
// enabled even when viewing a past year: Ifada (issues statements for any year,
// incl. alumni), the continuous programs (Daycare, Art School), the standalone
// Colonie, and Registration (fixed to the upcoming campaign year).
const YEAR_LOCK_EXEMPT_MODULES = new Set([
  "ifada", "daycare", "artschool", "colonie", "registration",
]);

// Wraps a write button so it looks & behaves disabled on a locked past year.
function YearLockedWrap({ children }) {
  return (
    <span
      className="relative inline-block align-middle opacity-40 cursor-not-allowed"
      title="Past academic year — read-only. Only an administrator can make changes."
    >
      <span className="pointer-events-none select-none">{children}</span>
    </span>
  );
}

export function Gate({ permission, label, adminOnly, children }) {
  const studio = useAccessStudio();

  // Provider missing -> fail CLOSED (hide). The provider is required; if you
  // ever see gated elements disappear everywhere, you forgot to wrap the app.
  if (!studio) return null;

  const { adminEnabled, mode, previewRole, userHasPermission, roleHas, showAdminOnly, yearReadOnly } = studio;

  // Admin-only element: governed by superuser status, not a granular permission.
  if (adminOnly) {
    if (adminEnabled && mode === "annotate") {
      return <AdminOnlyWrap label={label}>{children}</AdminOnlyWrap>;
    }
    return showAdminOnly ? <>{children}</> : null;
  }

  const [m, f, a] = permission.split(".");

  // Non-admins, or admin in plain Use mode: normal runtime gating.
  if (!adminEnabled || mode === "use") {
    if (!userHasPermission(m, f, a)) return null;
    // Past-year read-only: a write button shows disabled with a tooltip. The
    // backend enforces the lock regardless; this is just so staff don't click
    // into a 403.
    if (yearReadOnly && WRITE_ACTIONS.has(a) && !YEAR_LOCK_EXEMPT_MODULES.has(m)) {
      return <YearLockedWrap>{children}</YearLockedWrap>;
    }
    return <>{children}</>;
  }

  // Preview: render as the chosen role would see it.
  if (mode === "preview") {
    return previewRole && roleHas(previewRole, permission) ? <>{children}</> : null;
  }

  // Annotate: show the element with an overlay you click to assign.
  return <AnnotateWrap permission={permission} label={label}>{children}</AnnotateWrap>;
}

// Annotate-mode wrapper for admin-only elements: visible but NOT assignable.
function AdminOnlyWrap({ label, children }) {
  return (
    <span className="relative inline-block align-middle" title={`${label || "Admin only"} — restricted to Global Admin`}>
      <span className="opacity-95">{children}</span>
      <span className="absolute -inset-1 rounded-lg border-2 border-dashed border-slate-400/60 bg-slate-400/5 pointer-events-none z-10" />
      <span className="absolute -top-2 -right-2 z-20 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-slate-600 text-white text-[10px] font-semibold shadow pointer-events-none">
        <Crown size={9} /> admin
      </span>
    </span>
  );
}

function AnnotateWrap({ permission, label, children }) {
  const { rolesWith } = useAccessStudio();
  const [open, setOpen] = useState(false);
  const count = rolesWith(permission).length;
  const [, , action] = permission.split(".");

  return (
    <span className="relative inline-block align-middle">
      <span>{children}</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="absolute -inset-1 rounded-lg border-2 border-dashed border-indigo-400/70 bg-indigo-400/5 hover:bg-indigo-400/15 z-10"
        title={`${label || permission} — assign access`}
      />
      <span className="absolute -top-2 -right-2 z-20 flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-semibold shadow pointer-events-none">
        <Lock size={9} /> {count}
      </span>
      {open && (
        <AssignPopover permission={permission} label={label} action={action} onClose={() => setOpen(false)} />
      )}
    </span>
  );
}

function AssignPopover({ permission, label, action, onClose }) {
  const { roles, roleHas, toggleRole, saving } = useAccessStudio();
  return (
    <div className="absolute z-30 top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-white rounded-xl border border-slate-200 shadow-2xl p-3 text-left"
         onClick={(e) => e.stopPropagation()}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-xs font-bold text-slate-700">{label || permission}</p>
          <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded border capitalize ${toneFor(action)}`}>{action}</span>
          {saving && <span className="ml-2 text-[10px] text-slate-400">saving…</span>}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={15} /></button>
      </div>
      <p className="text-[11px] text-slate-400 mb-2">Who can use this?</p>
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {roles.map((r) => {
          const on = r.superuser || roleHas(r.code, permission);
          return (
            <button key={r.code} disabled={r.superuser}
              onClick={() => toggleRole(permission, r.code)}
              className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs border transition
                ${on ? "border-indigo-200 bg-indigo-50 text-indigo-700 font-medium" : "border-slate-200 text-slate-500 hover:border-slate-300"}
                ${r.superuser ? "opacity-70 cursor-default" : ""}`}>
              <span className="flex items-center gap-1.5">{r.superuser && <Crown size={11} className="text-indigo-600" />}{r.label}</span>
              {on ? <Check size={13} /> : <span className="w-3 h-3 rounded border border-slate-300" />}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-2 leading-snug">The action is fixed by this element — you only choose who.</p>
    </div>
  );
}

// ------------------------------------------------------------
// <ModuleAccessGate> — guards the MAIN CONTENT AREA for a whole module.
// Wrap your renderModule() output in this. If the current user (or, in
// preview mode, the previewed role) can't access the module, it shows a
// clean "no access" panel instead of rendering the module.
//
//   <ModuleAccessGate navConfig={NAVIGATION_CONFIG} currentModule={currentModule} user={user}>
//     {renderModule()}
//   </ModuleAccessGate>
//
// Access mirrors the sidebar rule: adminOnly -> superuser; allowedRoles ->
// role membership; otherwise the module's `view` (or alternate) permission.
// ------------------------------------------------------------
export function ModuleAccessGate({ navConfig, currentModule, user, children }) {
  const studio = useAccessStudio();
  if (!studio) return children;

  const item = (navConfig || []).find((i) => i.id === currentModule);
  if (!item) return children; // unknown module (e.g. infra screens) -> don't block

  const { mode, previewRole, roles, roleHas, userHasPermission, isSuperuser, adminEnabled } = studio;
  const previewing = adminEnabled && mode === "preview" && previewRole;
  const ALT_ACTIONS = ["view", "print", "create", "edit"];

  let canSee;
  if (previewing) {
    const role = roles.find((r) => r.code === previewRole);
    if (item.adminOnly) canSee = !!role?.superuser;
    else if (item.allowedRoles) canSee = !!role?.superuser || item.allowedRoles.includes(previewRole);
    else {
      canSee = roleHas(previewRole, `${item.module}.${item.feature}.view`);
      if (!canSee && item.alternateFeatures) {
        canSee = item.alternateFeatures.some((f) =>
          ALT_ACTIONS.some((a) => roleHas(previewRole, `${item.module}.${f}.${a}`)));
      }
    }
  } else {
    if (item.adminOnly) canSee = isSuperuser;
    else if (item.allowedRoles) canSee = isSuperuser || item.allowedRoles.some((r) => user?.roles?.includes(r));
    else {
      canSee = userHasPermission(item.module, item.feature, "view");
      if (!canSee && item.alternateFeatures) {
        canSee = item.alternateFeatures.some((f) =>
          ALT_ACTIONS.some((a) => userHasPermission(item.module, f, a)));
      }
    }
  }

  if (canSee) return children;

  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="bg-slate-100 p-4 rounded-full mb-4">
        <Lock size={28} className="text-slate-400" />
      </div>
      <h2 className="text-lg font-semibold text-slate-700">No access to {item.label}</h2>
      <p className="text-sm text-slate-500 mt-1 max-w-sm">
        {previewing
          ? `${roles.find((r) => r.code === previewRole)?.label || previewRole} can't open this module — this is what they'd see.`
          : "You don't have permission to view this module. Contact an administrator if you think this is a mistake."}
      </p>
    </div>
  );
}

// ------------------------------------------------------------
// Floating toolbar — only renders for admins once the studio is ready.
// ------------------------------------------------------------
export function AccessToolbar() {
  const studio = useAccessStudio();
  if (!studio?.adminEnabled) return null;
  const { ready, mode, setMode, previewRole, setPreviewRole, roles, saving } = studio;
  if (!ready) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white rounded-full border border-slate-200 shadow-lg px-2 py-1.5 flex items-center gap-1">
      {[
        { id: "use", label: "Use", icon: Eye },
        { id: "annotate", label: "Annotate", icon: MousePointerClick },
        { id: "preview", label: "Preview", icon: Users },
      ].map((m) => (
        <button key={m.id} onClick={() => setMode(m.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition
            ${mode === m.id ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <m.icon size={14} /> {m.label}
        </button>
      ))}
      {mode === "preview" && (
        <select value={previewRole || ""} onChange={(e) => setPreviewRole(e.target.value)}
          className="text-sm border border-slate-200 rounded-full px-3 py-1.5 bg-white ml-1">
          {roles.filter((r) => !r.superuser).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
      )}
      {saving && <span className="text-xs text-slate-400 px-2">saving…</span>}
    </div>
  );
}
